import type { GeoLine, GeoLineKind, GeoLineStyle } from '@wehunt/ui'
import { argbIntToHex, hexToArgbInt, LINE_DEFAULT_WIDTH } from '@wehunt/ui'

// CLUB-528: pure DTO <-> GeoLine mappers for the shared `lines` collection the
// mobile app already owns. This is where mobile's historical wire shapes are
// absorbed on read, so web can render documents mobile wrote before `pinType`
// existed, or before `trail` existed as a kind.

export interface TeamMapLineDto {
  id: string
  name: string | null
  description: string | null
  color: number | null
  lineWidth: number | null
  lineStyle: string | null
  pinType: string | null
  capStart: string | null
  capEnd: string | null
  coordinates: { latitude: number; longitude: number }[]
}

// R3: web never writes lineCap.start. Mobile renders a start cap
// (map_line_with_caps_widget.dart checks hasStart) and a legacy document can
// carry `start: 'arrow'` for a double-headed arrow — web has no control for
// one, so emitting any value here would silently erase that legacy start cap.
// capStart therefore stays read-only (see TeamMapLineDto above) and is never
// part of the write body.
export interface TeamMapLineWriteBody {
  name: string | null
  description: string | null
  color: number
  lineWidth: number
  lineStyle: GeoLineStyle
  pinType: GeoLineKind
  capEnd: 'none' | 'arrow'
  coordinates: { latitude: number; longitude: number }[]
}

const KNOWN_STYLES: GeoLineStyle[] = ['solid', 'dashed', 'dotted', 'dash_dot']
const MIN_COORDINATES = 2

/** Null for a document web cannot render — every caller drops it and keeps the
 * rest of the collection, so one malformed line never blanks the whole map.
 *
 * Note that web only SKIPS; it does not report. The mobile app's own reader
 * logs each skipped document, and the API skips and logs server-side too, so
 * a malformed document is already visible where it can be acted on. Adding a
 * third report from the browser would duplicate that signal without adding a
 * document id anyone could act on more usefully. */
export function dtoToGeoLine(dto: TeamMapLineDto): GeoLine | null {
  const coords = dto.coordinates ?? []
  if (coords.length < MIN_COORDINATES) return null

  const kind: GeoLineKind =
    dto.pinType === 'trail' ? 'trail'
    : dto.pinType === 'arrow' ? 'arrow'
    : dto.pinType === 'line' ? 'line'
    // Pre-pinType documents encode an arrow through its caps.
    : dto.capEnd === 'arrow' || dto.capStart === 'arrow' ? 'arrow'
    : 'line'

  const style = KNOWN_STYLES.find((s) => s === dto.lineStyle) ?? 'solid'

  return {
    id: dto.id,
    name: dto.name ?? undefined,
    description: dto.description ?? undefined,
    kind,
    color: argbIntToHex(dto.color ?? undefined),
    width: dto.lineWidth ?? LINE_DEFAULT_WIDTH,
    style,
    points: coords.map((c) => ({ lat: c.latitude, lon: c.longitude })),
  }
}

export function geoLineToWriteBody(line: GeoLine): TeamMapLineWriteBody {
  return {
    name: line.name ?? null,
    description: line.description ?? null,
    color: hexToArgbInt(line.color),
    lineWidth: line.width ?? LINE_DEFAULT_WIDTH,
    lineStyle: line.style ?? 'solid',
    pinType: line.kind,
    // Caps derive from kind so they always agree with pinType — the app still
    // infers kind from caps on older documents, so disagreeing values would
    // make one client read the line as the wrong type.
    capEnd: line.kind === 'arrow' ? 'arrow' : 'none',
    coordinates: line.points.map((p) => ({ latitude: p.lat, longitude: p.lon })),
  }
}
